import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addDashboardBlock,
  addDashboardBlockFromPrompt,
  getDashboardCatalog,
} from '@/api/dashboards';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import type {
  DashboardBlockSize,
  DashboardCatalogEntry,
  DashboardTextLayout,
  DashboardVisualization,
  ChartType,
} from '@pm-next/shared-types';

interface AddBlockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboardId: string;
}

export function AddBlockDialog({ open, onOpenChange, dashboardId }: AddBlockDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [toolName, setToolName] = useState<string>('');
  const [title, setTitle] = useState('');
  const [visualization, setVisualization] = useState<DashboardVisualization>('chart');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [size, setSize] = useState<DashboardBlockSize>('md');
  const [hideFilters, setHideFilters] = useState(true);
  const [textLayout, setTextLayout] = useState<DashboardTextLayout>('cards');
  const [prompt, setPrompt] = useState('');

  const { data: catalog = [] } = useQuery({
    queryKey: ['dashboard-catalog'],
    queryFn: getDashboardCatalog,
    enabled: open,
  });

  const selected: DashboardCatalogEntry | undefined = catalog.find((c) => c.toolName === toolName);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] });
    queryClient.invalidateQueries({ queryKey: ['dashboards'] });
  };

  const catalogAdd = useMutation({
    mutationFn: () =>
      addDashboardBlock(dashboardId, {
        title: title || selected?.label || 'Block',
        toolName,
        visualization,
        chartType: visualization === 'chart' ? chartType : undefined,
        size,
        filters: selected?.filters,
        toolArgs: selected?.defaultToolArgs,
        config: {
          version: 1,
          display:
            selected?.defaultValueMode || selected?.defaultFormatterId
              ? {
                  valueMode: selected.defaultValueMode,
                  formatterId: selected.defaultFormatterId,
                }
              : undefined,
          ui: { hideFilters },
          chart:
            visualization === 'chart'
              ? {
                  colorScheme: 'default',
                  showLabels: false,
                  showLegend: true,
                  ...(selected?.defaultSeriesKeys?.length
                    ? { seriesKeys: selected.defaultSeriesKeys }
                    : {}),
                }
              : undefined,
          text: visualization === 'text' ? { layout: textLayout } : undefined,
        },
      }),
    onSuccess: () => {
      invalidate();
      onOpenChange(false);
      reset();
      toast({ title: 'Block added' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Failed to add block', description: err.message });
    },
  });

  const promptAdd = useMutation({
    mutationFn: () => addDashboardBlockFromPrompt(dashboardId, { prompt }),
    onSuccess: () => {
      invalidate();
      onOpenChange(false);
      reset();
      toast({ title: 'Block created from prompt' });
    },
    onError: (err: Error) => {
      toast({
        variant: 'destructive',
        title: 'Prompt failed',
        description: err.message,
      });
    },
  });

  const reset = () => {
    setToolName('');
    setTitle('');
    setVisualization('chart');
    setChartType('bar');
    setSize('md');
    setHideFilters(true);
    setTextLayout('cards');
    setPrompt('');
  };

  const onSelectTool = (name: string) => {
    setToolName(name);
    const entry = catalog.find((c) => c.toolName === name);
    if (entry) {
      setTitle(entry.label);
      setVisualization(entry.defaultVisualization);
      if (entry.defaultChartType) setChartType(entry.defaultChartType);
      setSize(entry.defaultSize);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add block</DialogTitle>
          <DialogDescription>
            Pick a data source from the catalog, or describe what you want with a prompt.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="catalog">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="catalog">Catalog</TabsTrigger>
            <TabsTrigger value="prompt">Prompt</TabsTrigger>
          </TabsList>

          <TabsContent value="catalog" className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label>Data source</Label>
              <Select value={toolName} onValueChange={onSelectTool}>
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {catalog.map((c) => (
                    <SelectItem key={c.toolName} value={c.toolName}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selected && <p className="text-xs text-muted-foreground">{selected.description}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Visualization</Label>
                <Select
                  value={visualization}
                  onValueChange={(v) => setVisualization(v as DashboardVisualization)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(selected?.allowedVisualizations || ['chart', 'list', 'text']).map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Size</Label>
                <Select value={size} onValueChange={(v) => setSize(v as DashboardBlockSize)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['sm', 'md', 'lg', 'full'] as DashboardBlockSize[]).map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {visualization === 'chart' && (
              <div className="space-y-1.5">
                <Label>Chart type</Label>
                <Select value={chartType} onValueChange={(v) => setChartType(v as ChartType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['bar', 'line', 'pie', 'donut', 'area', 'stack'] as ChartType[]).map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {visualization === 'text' && (
              <div className="space-y-1.5">
                <Label>Text layout</Label>
                <Select
                  value={textLayout}
                  onValueChange={(v) => setTextLayout(v as DashboardTextLayout)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cards">Cards</SelectItem>
                    <SelectItem value="list">Key / value list</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {!!selected?.filters?.length && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={!hideFilters}
                  onCheckedChange={(v) => setHideFilters(v !== true)}
                />
                Show filters on dashboard
              </label>
            )}

            <DialogFooter>
              <Button
                type="button"
                disabled={!toolName || catalogAdd.isPending}
                onClick={() => catalogAdd.mutate()}
              >
                {catalogAdd.isPending ? 'Adding…' : 'Add block'}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="prompt" className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label>Describe the block</Label>
              <textarea
                className="min-h-[120px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder='e.g. "Add a bar chart for employees by department"'
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Uses AI once to build the block config. Token usage is logged. Data loads without
                AI.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                disabled={!prompt.trim() || promptAdd.isPending}
                onClick={() => promptAdd.mutate()}
              >
                {promptAdd.isPending ? 'Creating…' : 'Create from prompt'}
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
